import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, Globe, Map, Clock, Award, ChevronRight } from 'lucide-react';
import GuideApplicationModal from './GuideApplicationModal';

const BecomeGuide = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  
  return (
    <section id="become-guide-section" className="relative py-12 overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{ 
          backgroundImage: 'url(https://imgur.com/loswZvI.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Content */}
          <div className="lg:col-span-7 text-white">
            <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
              <Award className="h-3 w-3 mr-1" />
              {t('becomeGuide.subtitle')}
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">
              Become a Uganda Safari & Tour Guide
            </h2>
            
            <p className="text-white/80 mb-6 text-sm md:text-base">
              Turn your passion for Uganda's wildlife and culture into a rewarding career. Join our community of expert safari guides specializing in gorilla trekking, game drives, and authentic local experiences.
            </p>

            {/* Benefits Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Benefit 1 */}
              <div className="flex items-start space-x-3 group">
                <div className="flex-shrink-0 w-8 h-8 bg-ta-green/20 rounded-lg flex items-center justify-center backdrop-blur-sm group-hover:bg-ta-green/30 transition-colors">
                  <DollarSign className="h-4 w-4 text-ta-green" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-0.5">{t('becomeGuide.benefits.earnings')}</h3>
                  <p className="text-xs text-white/70">{t('becomeGuide.benefits.earningsDesc')}</p>
                </div>
              </div>

              {/* Benefit 2 */}
              <div className="flex items-start space-x-3 group">
                <div className="flex-shrink-0 w-8 h-8 bg-ta-green/20 rounded-lg flex items-center justify-center backdrop-blur-sm group-hover:bg-ta-green/30 transition-colors">
                  <Globe className="h-4 w-4 text-ta-green" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-0.5">{t('becomeGuide.benefits.reach')}</h3>
                  <p className="text-xs text-white/70">{t('becomeGuide.benefits.reachDesc')}</p>
                </div>
              </div>

              {/* Benefit 3 */}
              <div className="flex items-start space-x-3 group">
                <div className="flex-shrink-0 w-8 h-8 bg-ta-green/20 rounded-lg flex items-center justify-center backdrop-blur-sm group-hover:bg-ta-green/30 transition-colors">
                  <Map className="h-4 w-4 text-ta-green" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-0.5">{t('becomeGuide.benefits.share')}</h3>
                  <p className="text-xs text-white/70">{t('becomeGuide.benefits.shareDesc')}</p>
                </div>
              </div>

              {/* Benefit 4 */}
              <div className="flex items-start space-x-3 group">
                <div className="flex-shrink-0 w-8 h-8 bg-ta-green/20 rounded-lg flex items-center justify-center backdrop-blur-sm group-hover:bg-ta-green/30 transition-colors">
                  <Clock className="h-4 w-4 text-ta-green" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-0.5">{t('becomeGuide.benefits.flexible')}</h3>
                  <p className="text-xs text-white/70">{t('becomeGuide.benefits.flexibleDesc')}</p>
                </div>
              </div>
            </div>

            <button 
              onClick={() => setIsModalOpen(true)}
              className="group inline-flex items-center bg-ta-green text-white px-4 py-2 text-sm rounded-lg hover:bg-ta-green-dark transition-all duration-300 transform hover:translate-y-[-2px]"
            >
              Become a Uganda Safari Guide Today
              <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Stats/Features Card */}
          <div className="lg:col-span-5 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-ta-green/20 to-transparent rounded-2xl transform -rotate-2 scale-105"></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                  <div className="text-2xl font-bold text-ta-green mb-0.5">100+</div>
                  <div className="text-xs text-white/80">{t('becomeGuide.stats.guides')}</div>
                </div>
                <div className="text-center p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                  <div className="text-2xl font-bold text-ta-green mb-0.5">50k+</div>
                  <div className="text-xs text-white/80">{t('becomeGuide.stats.travelers')}</div>
                </div>
                <div className="text-center p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                  <div className="text-2xl font-bold text-ta-green mb-0.5">4.8/5</div>
                  <div className="text-xs text-white/80">{t('becomeGuide.stats.rating')}</div>
                </div>
                <div className="text-center p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                  <div className="text-2xl font-bold text-ta-green mb-0.5">85%</div>
                  <div className="text-xs text-white/80">{t('becomeGuide.stats.commission')}</div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-center text-white/90 text-sm">
                  <div className="w-3 h-3 rounded-full bg-ta-green/20 flex items-center justify-center mr-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-ta-green"></div>
                  </div>
                  <span className="text-xs">Professional safari guide profile</span>
                </div>
                <div className="flex items-center text-white/90 text-sm">
                  <div className="w-3 h-3 rounded-full bg-ta-green/20 flex items-center justify-center mr-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-ta-green"></div>
                  </div>
                  <span className="text-xs">Secure payment processing</span>
                </div>
                <div className="flex items-center text-white/90 text-sm">
                  <div className="w-3 h-3 rounded-full bg-ta-green/20 flex items-center justify-center mr-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-ta-green"></div>
                  </div>
                  <span className="text-xs">Global marketing for your tours</span>
                </div>
                <div className="flex items-center text-white/90 text-sm">
                  <div className="w-3 h-3 rounded-full bg-ta-green/20 flex items-center justify-center mr-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-ta-green"></div>
                  </div>
                  <span className="text-xs">24/7 guide support team</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guide Application Modal */}
      <GuideApplicationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default BecomeGuide;